import type { GuestCoordinates, AxisSelection } from '../types/pca';

// Distinct color palette for 10 clusters
// Using warm, muted tones that are clearly distinguishable but not flamboyant
const CLUSTER_COLORS: Record<number, string> = {
  1: '#D97757',  // Warm coral
  2: '#4A7C59',  // Forest green
  3: '#B8860B',  // Dark goldenrod
  4: '#6B4C93',  // Muted purple
  5: '#C9A961',  // Muted gold
  6: '#4A90A4',  // Teal blue
  7: '#A0522D',  // Sienna
  8: '#5F8A5F',  // Medium green
  9: '#B87333',  // Copper
  10: '#6B7B8C', // Slate blue-gray
};

export function getColorForCluster(cluster: number | undefined): string {
  if (!cluster || !CLUSTER_COLORS[cluster]) {
    return '#D4A574'; // Default warm tan
  }
  return CLUSTER_COLORS[cluster];
}

export function getClusterName(cluster: number): string {
  // Placeholder cluster names - can be updated with actual profiles
  const names: Record<number, string> = {
    1: 'Growth & Marketing',
    2: 'AI & Innovation',
    3: 'Product Craft',
    4: 'Personal Growth',
    5: 'Sales & GTM',
    6: 'Communication',
    7: 'Strategists',
    8: 'Product Leaders',
    9: 'Builders',
    10: 'Operators',
  };
  return names[cluster] || `Cluster ${cluster}`;
}

export function getColorForDimension(
  value: number,
  min: number,
  max: number
): string {
  // Normalize value to 0-1 range
  const normalized = (value - min) / (max - min);
  
  // Create a warm color gradient: yellow (low) to orange (mid) to deep orange (high)
  // Yellow: hsl(45, 100%, 50%) -> Orange: hsl(25, 100%, 50%) -> Deep Orange: hsl(15, 100%, 50%)
  const hue = 45 - (normalized * 30); // Yellow (45) to Deep Orange (15)
  const saturation = 0.85 + (normalized * 0.15); // 85% to 100%
  const lightness = 0.45 + (normalized * 0.1); // 45% to 55%
  
  return `hsl(${hue}, ${saturation * 100}%, ${lightness * 100}%)`;
}

export function getColorForGuest(
  guest: GuestCoordinates,
  axisSelection: AxisSelection,
  minMax: { min: number; max: number }
): string {
  // Use the average of the three selected dimensions
  const x = guest.coordinates[axisSelection.x - 1] || 0;
  const y = guest.coordinates[axisSelection.y - 1] || 0;
  const z = guest.coordinates[axisSelection.z - 1] || 0;
  
  const avg = (x + y + z) / 3;
  return getColorForDimension(avg, minMax.min, minMax.max);
}

export function calculateMinMax(
  guests: GuestCoordinates[],
  axisSelection: AxisSelection
): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  
  guests.forEach(guest => {
    const x = guest.coordinates[axisSelection.x - 1] || 0;
    const y = guest.coordinates[axisSelection.y - 1] || 0;
    const z = guest.coordinates[axisSelection.z - 1] || 0;
    const avg = (x + y + z) / 3;
    
    min = Math.min(min, avg);
    max = Math.max(max, avg);
  });
  
  return { min, max };
}

export function getPointSize(
  variance?: number,
  popularity?: number,
  usePopularity: boolean = false
): number {
  // Base size
  const baseSize = 0.15;
  
  // If using popularity scaling:
  // - Wider range: 0.35x to 4.5x for dramatic visual difference
  // - Power curve (^1.5) to make popular episodes stand out even more
  // - Result: Nikita Bier (340k) will be ~13x larger than low-view episodes
  if (usePopularity && popularity !== undefined) {
    const minMultiplier = 0.35;  // Less popular episodes (small but visible)
    const maxMultiplier = 4.5;   // Most popular episodes (really big!)
    // Apply power curve to exaggerate differences at the top
    const curvedPopularity = Math.pow(popularity, 1.5);
    const sizeMultiplier = minMultiplier + (curvedPopularity * (maxMultiplier - minMultiplier));
    return baseSize * sizeMultiplier;
  }
  
  // Legacy variance scaling (if needed)
  if (variance) {
    return baseSize * (1 + variance * 2);
  }
  
  return baseSize;
}
